// src/features/monetize/components/ViewerLedgerPanel.tsx

import { useState, type ReactNode } from 'react';
import { ActionIcon, Alert, Badge, Button, Group, Menu, Paper, Skeleton, Stack, Table, Text, Title } from '@mantine/core';
import { IconBan, IconCalendarPlus, IconCreditCardOff, IconDotsVertical, IconGift, IconReceiptRefund } from '@tabler/icons-react';
import {
  ENTITLEMENT_STATUS_COLORS,
  ENTITLEMENT_STATUS_LABELS,
  PURCHASE_STATUS_COLORS,
  SUBSCRIPTION_STATUS_COLORS,
  SUBSCRIPTION_STATUS_LABELS,
} from '@/lib/constants/monetize';
import type { MonetizeEntitlement, MonetizePurchase, MonetizeSubscription } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { useMonetizeCatalog } from '../useMonetizeCatalog';
import { useViewerLedger } from '../useViewerLedger';
import { CompDrawer } from './CompDrawer';
import { CancelModal, ExtendModal, RefundModal, RevokeModal } from './LedgerActionModals';
import { useScopeLookups } from './ScopePicker';

type Action =
  | { kind: 'revoke'; entitlement: MonetizeEntitlement }
  | { kind: 'extend'; entitlement: MonetizeEntitlement }
  | { kind: 'refund'; purchase: MonetizePurchase }
  | { kind: 'cancel'; subscription: MonetizeSubscription }
  | null;

export function ViewerLedgerPanel({ viewerId }: { viewerId: string }) {
  const { ledger, isLoading, isError, error } = useViewerLedger(viewerId);
  const { catalog } = useMonetizeCatalog();
  const { lookups } = useScopeLookups();
  const [compOpen, setCompOpen] = useState(false);
  const [action, setAction] = useState<Action>(null);

  if (isError) return <Alert color="red">{error instanceof Error ? error.message : 'Could not load the viewer.'}</Alert>;
  if (isLoading || !ledger) return <Skeleton height={240} />;

  const productName = new Map((catalog?.products ?? []).map((p) => [p.id, p.name]));
  const nameOf = (id: string) => productName.get(id) ?? 'Product';
  const { viewer, entitlements, purchases, subscriptions, trail } = ledger;
  const close = () => setAction(null);

  return (
    <Stack gap="lg">
      <Paper withBorder p="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Group gap="xs">
              <Title order={4}>{viewer.email}</Title>
              <Badge variant="light" color={viewer.status === 'active' ? 'green' : 'red'} size="sm">{viewer.status === 'active' ? 'Active' : viewer.status}</Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Signed up {MonetizeUtils.formatWhen(viewer.created)}{viewer.lastSignInAt ? ` · last sign-in ${MonetizeUtils.formatWhen(viewer.lastSignInAt)}` : ''}
            </Text>
          </Stack>
          <Button leftSection={<IconGift size={16} />} onClick={() => setCompOpen(true)}>Grant access</Button>
        </Group>
      </Paper>

      <Section title="Subscriptions" count={subscriptions.length} empty="No subscriptions.">
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Started</Table.Th>
              <Table.Th w={48} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {subscriptions.map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td><Text size="sm">{nameOf(s.productId)}</Text></Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Badge variant="light" size="sm" color={SUBSCRIPTION_STATUS_COLORS[s.status] ?? 'gray'}>{SUBSCRIPTION_STATUS_LABELS[s.status] ?? s.status}</Badge>
                    <Text size="sm" c="dimmed">{MonetizeUtils.subscriptionSummary(s)}</Text>
                  </Group>
                </Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{MonetizeUtils.formatWhen(s.created)}</Text></Table.Td>
                <Table.Td>
                  {s.cancelable && (
                    <RowMenu label="Subscription actions">
                      <Menu.Item color="red" leftSection={<IconCreditCardOff size={14} />} onClick={() => setAction({ kind: 'cancel', subscription: s })}>Cancel subscription…</Menu.Item>
                    </RowMenu>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Section>

      <Section title="Purchases" count={purchases.length} empty="No purchases.">
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>Amount</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Paid</Table.Th>
              <Table.Th w={48} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {purchases.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td><Text size="sm">{nameOf(p.productId)}</Text></Table.Td>
                <Table.Td><Text size="sm">{p.amount === 0 ? 'Free (promo code)' : MonetizeUtils.formatAmount(p.amount, p.currency)}</Text></Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Badge variant="light" size="sm" color={PURCHASE_STATUS_COLORS[p.status] ?? 'gray'}>{MonetizeUtils.purchaseStatusLabel(p)}</Badge>
                  </Group>
                </Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{MonetizeUtils.formatWhen(p.created)}</Text></Table.Td>
                <Table.Td>
                  {p.refundable > 0 && (
                    <RowMenu label="Purchase actions">
                      <Menu.Item leftSection={<IconReceiptRefund size={14} />} onClick={() => setAction({ kind: 'refund', purchase: p })}>Refund…</Menu.Item>
                    </RowMenu>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Section>

      <Section title="Access" count={entitlements.length} empty="No access rows yet. Grant access, or wait for a purchase.">
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>What</Table.Th>
              <Table.Th>How</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Window</Table.Th>
              <Table.Th>Source</Table.Th>
              <Table.Th w={48} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {entitlements.map((e) => {
              const canRevoke = e.status === 'active' || e.status === 'suspended';
              const canExtend = !!e.endsAt && e.status !== 'revoked';
              return (
                <Table.Tr key={e.id} style={e.status === 'revoked' || e.status === 'expired' ? { opacity: 0.6 } : undefined}>
                  <Table.Td><Text size="sm">{MonetizeUtils.scopeLabel(e.scope, lookups)}</Text></Table.Td>
                  <Table.Td><Text size="sm">{MonetizeUtils.accessModeLabel(e.accessMode)}</Text></Table.Td>
                  <Table.Td><Badge variant="light" size="sm" color={ENTITLEMENT_STATUS_COLORS[e.status] ?? 'gray'}>{ENTITLEMENT_STATUS_LABELS[e.status] ?? e.status}</Badge></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{MonetizeUtils.entitlementWindow(e)}</Text></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{MonetizeUtils.entitlementSourceLabel(e)}</Text></Table.Td>
                  <Table.Td>
                    {(canRevoke || canExtend) && (
                      <RowMenu label="Access actions">
                        {canExtend && <Menu.Item leftSection={<IconCalendarPlus size={14} />} onClick={() => setAction({ kind: 'extend', entitlement: e })}>Extend…</Menu.Item>}
                        {canRevoke && <Menu.Item color="red" leftSection={<IconBan size={14} />} onClick={() => setAction({ kind: 'revoke', entitlement: e })}>Revoke…</Menu.Item>}
                      </RowMenu>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Section>

      <Section title="History" count={trail.length} empty="Nothing yet.">
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>When</Table.Th>
              <Table.Th>Who</Table.Th>
              <Table.Th>What</Table.Th>
              <Table.Th>Why</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {trail.map((a) => (
              <Table.Tr key={a.id}>
                <Table.Td><Text size="sm" c="dimmed">{MonetizeUtils.formatWhen(a.created)}</Text></Table.Td>
                <Table.Td><Text size="sm">{MonetizeUtils.actorLabel(a)}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm">{MonetizeUtils.auditActionLabel(a.action)}</Text>
                  <Text size="xs" c="dimmed">{a.targetType}</Text>
                </Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{a.reason}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Section>

      <CompDrawer opened={compOpen} viewerId={viewerId} viewerEmail={viewer.email} onClose={() => setCompOpen(false)} />
      {action?.kind === 'revoke' && <RevokeModal viewerId={viewerId} entitlement={action.entitlement} label={MonetizeUtils.scopeLabel(action.entitlement.scope, lookups)} onClose={close} />}
      {action?.kind === 'extend' && <ExtendModal viewerId={viewerId} entitlement={action.entitlement} label={MonetizeUtils.scopeLabel(action.entitlement.scope, lookups)} onClose={close} />}
      {action?.kind === 'refund' && <RefundModal viewerId={viewerId} purchase={action.purchase} productName={nameOf(action.purchase.productId)} onClose={close} />}
      {action?.kind === 'cancel' && <CancelModal viewerId={viewerId} subscription={action.subscription} productName={nameOf(action.subscription.productId)} onClose={close} />}
    </Stack>
  );
}

function Section({ title, count, empty, children }: { title: string; count: number; empty: string; children: ReactNode }) {
  return (
    <Stack gap="xs">
      <Title order={5}>{title}</Title>
      {count === 0 ? <Text size="sm" c="dimmed">{empty}</Text> : <Paper withBorder style={{ overflow: 'clip' }}>{children}</Paper>}
    </Stack>
  );
}

function RowMenu({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" aria-label={label}><IconDotsVertical size={16} /></ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>{children}</Menu.Dropdown>
    </Menu>
  );
}
